/**
 * À propos — page institutionnelle BCL Telecom & Énergie.
 * Histoire, mission, valeurs, chiffres clés, partenaires.
 */
import { Award, CheckCircle2, Compass, Handshake, Sparkles, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MarketingLayout } from '@/components/site/marketing-layout';

const stats = [
  { value: '100%', label: 'Conformité ARCEP' },
  { value: '8 Gb/s', label: 'Débit fibre maximum' },
  { value: '3', label: 'Opérateurs partenaires' },
  { value: '24 h', label: 'Délai de réponse devis' },
];

const values = [
  {
    title: 'Expertise réglementaire',
    description:
      'Référencés ARCEP, nos équipes maîtrisent les démarches administratives auprès des opérateurs et des collectivités.',
    icon: Award,
  },
  {
    title: 'Accompagnement de bout en bout',
    description:
      "De l'étude de faisabilité au PV de réception, vous avez un interlocuteur unique qui pilote votre projet.",
    icon: Compass,
  },
  {
    title: 'Transparence',
    description:
      'Tarifs publics, étapes du dossier visibles dans votre espace client, comptes rendus partagés à chaque jalon.',
    icon: Sparkles,
  },
  {
    title: 'Partenariat opérateurs',
    description:
      'Relations directes avec Orange, SFR et Free pour fluidifier le raccordement final.',
    icon: Handshake,
  },
];

const milestones = [
  {
    title: 'Notre mission',
    description:
      "Simplifier l'adduction télécoms pour les porteurs de projets immobiliers — particuliers, promoteurs, aménageurs et copropriétés — en prenant en charge l'intégralité du parcours technique et administratif.",
  },
  {
    title: 'Notre méthode',
    description:
      "Une qualification rapide de votre demande, un chiffrage transparent, des documents validés par notre bureau d'études, des techniciens référencés, et un suivi temps réel dans votre espace client.",
  },
  {
    title: 'Notre engagement',
    description:
      'Respect des délais contractuels, conformité ARCEP, traçabilité de chaque pièce du dossier, et un support disponible 7j/7 pour vos chantiers actifs.',
  },
];

export function AboutPage() {
  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="bg-gradient-to-b from-white to-bcl-grey/40 py-20">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-4">
              À propos
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-balance">
              Un bureau d&apos;études dédié à l&apos;adduction télécoms
            </h1>
            <p className="text-lg text-gray-600">
              BCL Telecom &amp; Énergie accompagne particuliers, promoteurs,
              aménageurs et copropriétés dans la conception, la coordination et
              le suivi de leurs projets de raccordement fibre optique.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {stats.map((s) => (
              <Card key={s.label} className="text-center">
                <CardContent className="py-6">
                  <p className="text-3xl font-bold text-bcl-blue">{s.value}</p>
                  <p className="mt-1 text-sm text-gray-600">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION / MÉTHODE / ENGAGEMENT */}
      <section className="py-20 bg-white">
        <div className="container px-4 mx-auto">
          <div className="grid gap-6 md:grid-cols-3">
            {milestones.map((m) => (
              <Card key={m.title} className="border-gray-100">
                <CardHeader>
                  <div className="grid h-10 w-10 place-items-center rounded-md bg-bcl-blue/10 text-bcl-blue mb-2">
                    <Target className="h-5 w-5" />
                  </div>
                  <CardTitle>{m.title}</CardTitle>
                  <CardDescription>{m.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* VALEURS */}
      <section className="py-20 bg-bcl-grey/30">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Nos valeurs</h2>
            <p className="text-lg text-gray-600">
              Ce qui guide la manière dont nous menons vos projets, de la
              qualification à la livraison.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {values.map((v) => (
              <div
                key={v.title}
                className="flex gap-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm"
              >
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-bcl-blue/10 text-bcl-blue">
                  <v.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">{v.title}</h3>
                  <p className="text-sm text-gray-600">{v.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTENAIRES */}
      <section className="py-20 bg-white">
        <div className="container px-4 mx-auto">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">
                Référencés par l&apos;ARCEP
              </h2>
              <p className="text-gray-600 mb-4">
                BCL Telecom est officiellement référencé par l&apos;Autorité de
                Régulation des Communications Électroniques, des Postes et de
                la distribution de la presse. Ce référencement garantit notre
                conformité aux exigences réglementaires nationales et notre
                capacité à intervenir sur les infrastructures fibre.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bcl-teal" />
                  <span>Habilités à monter les dossiers de raccordement collectif</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bcl-teal" />
                  <span>Accès aux interlocuteurs techniques des opérateurs</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bcl-teal" />
                  <span>Suivi des incidents et procédures de médiation</span>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 mb-4">
                Partenariats opérateurs
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-xl border border-gray-100 bg-white p-6 text-center shadow-sm">
                  <p className="font-bold text-orange-500 text-xl">Orange</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white p-6 text-center shadow-sm">
                  <p className="font-bold text-red-500 text-xl">SFR</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white p-6 text-center shadow-sm">
                  <p className="font-bold text-blue-500 text-xl">Free</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-bcl-grey">
        <div className="container px-4 mx-auto max-w-4xl">
          <div className="rounded-2xl bg-bcl-blue p-10 text-white text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-balance">
              Un projet&nbsp;? Parlons-en.
            </h2>
            <p className="text-white/85 mb-6 max-w-xl mx-auto">
              Décrivez votre adresse et la nature de vos travaux, nous revenons
              vers vous sous 24 h ouvrées avec une qualification de votre
              demande et un devis adapté.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup">
                <Button size="lg" variant="secondary">Créer mon compte</Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-white text-white hover:bg-white/10"
                >
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
